import axios from "axios";
import store from "../store";

class TicketService {
  
  /**
   * List users for ticket assignment
   */
  async listUsers() {
    const jwt = store.state.jwt;
    
    return new Promise((resolve, reject) => {
      axios.get(`${process.env.VUE_APP_SAAS_BACK}/ticket/users`, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * List unique projects from tickets
   */
  async listProjects(clientUid = null) {
    const jwt = store.state.jwt;
    
    const params = {};
    if (clientUid) {
      params.client_uid = clientUid;
    }
    
    return new Promise((resolve, reject) => {
      axios.get(`${process.env.VUE_APP_SAAS_BACK}/ticket/projects`, {
        params,
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * List tickets with filters and pagination
   */
  async listTickets(filters = {}) {
    const jwt = store.state.jwt;
    const isAdmin = !!(store.state.user && store.state.user.details && store.state.user.details.client_uid);
    
    const params = {
      statut: filters.statut || '',
      priorite: filters.priorite || '',
      type: filters.type || '',
      projet: filters.projet || '',
      assigned_to: filters.assigned_to || '',
      search: filters.search || '',
      all_clients: filters.allClients ? 'true' : '',
      sort: filters.sort || 'date',
      order: filters.order || 'DESC',
      page: filters.page || 1,
      limit: filters.limit || 20,
      client_uid: (isAdmin && filters.client_uid) ? filters.client_uid : store.state.user.uid,
      only_followed: filters.only_followed ? 'true' : '',
      only_unread: filters.only_unread ? 'true' : '',
      hide_sub_tickets: filters.hide_sub_tickets ? 'true' : ''
    };

    return new Promise((resolve, reject) => {
      axios.get(`${process.env.VUE_APP_SAAS_BACK}/ticket/list`, {
        params,
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Get a single ticket by ID
   */
  async getTicket(ticketId) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.get(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Create a new ticket
   */
  async createTicket(ticketData) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/create`, {
        ...ticketData,
        // Only set client_uid from store if not explicitly provided (admin can override)
        client_uid: ticketData.client_uid || store.state.user.client_uid
      }, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Update an existing ticket
   */
  async updateTicket(ticketId, ticketData) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.put(`${process.env.VUE_APP_SAAS_BACK}/ticket/update/${ticketId}`, ticketData, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Delete a ticket
   */
  async deleteTicket(ticketId) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.delete(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Assign a ticket to a user
   */
  async assignTicket(ticketId, assignedTo) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.put(`${process.env.VUE_APP_SAAS_BACK}/ticket/assign/${ticketId}`, {
        assigned_to: assignedTo
      }, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Upload files to a ticket
   */
  async uploadTicketFiles(ticketId, files) {
    const jwt = store.state.jwt;
    const formData = new FormData();
    
    for (const file of files) {
      formData.append('files', file);
    }

    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/files`, formData, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Delete a file from a ticket
   */
  async deleteTicketFile(ticketId, fileKey) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.delete(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/files`, {
        headers: {
          Authorization: `Bearer ${jwt}`
        },
        data: { fileKey }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Create a message for a ticket
   */
  async createMessage(ticketId, message) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/message/create`, {
        ticket_id: ticketId,
        message
      }, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Update a message
   */
  async updateMessage(messageId, messageData) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.put(`${process.env.VUE_APP_SAAS_BACK}/ticket/message/update/${messageId}`, messageData, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Delete a message
   */
  async deleteMessage(messageId) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.delete(`${process.env.VUE_APP_SAAS_BACK}/ticket/message/${messageId}`, {
        headers: {
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Upload files to a message
   */
  async uploadMessageFiles(messageId, files) {
    const jwt = store.state.jwt;
    const formData = new FormData();
    
    for (const file of files) {
      formData.append('files', file);
    }

    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/message/${messageId}/files`, formData, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Delete a file from a message
   */
  async deleteMessageFile(messageId, fileKey) {
    const jwt = store.state.jwt;

    return new Promise((resolve, reject) => {
      axios.delete(`${process.env.VUE_APP_SAAS_BACK}/ticket/message/${messageId}/files`, {
        headers: {
          Authorization: `Bearer ${jwt}`
        },
        data: { fileKey }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Get followers of a ticket
   */
  async getFollowers(ticketId) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.get(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/followers`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Follow a ticket
   */
  async followTicket(ticketId) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/follow`, {}, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  /**
   * Unfollow a ticket
   */
  async unfollowTicket(ticketId) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.delete(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/follow`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  async markAllSeen() {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/mark-all-seen`, {}, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  async listTemps(ticketId) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.get(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/temps`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  async addTemps(ticketId, data) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.post(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/temps`, data, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  async updateTemps(ticketId, tacheId, data) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.put(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/temps/${tacheId}`, data, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }

  async deleteTemps(ticketId, tacheId) {
    const jwt = store.state.jwt;
    return new Promise((resolve, reject) => {
      axios.delete(`${process.env.VUE_APP_SAAS_BACK}/ticket/${ticketId}/temps/${tacheId}`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      .then(response => resolve(response.data))
      .catch(error => reject(error));
    });
  }
}

export default new TicketService();
